import { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from './SplitFlap';

/**
 * RingGauge - a pure-SVG donut gauge for headline rate metrics.
 *
 * A track ring plus a foreground arc whose sweep is the percentage. The centre
 * carries the formatted value and an optional caption. Used by both Flight Deck
 * card styles; renders well on dark (glass card) and light (dashboard row).
 */

export interface RingGaugeProps {
  /** 0..100 fill. null renders an empty, dimmed ring (no-data state). */
  pct: number | null;
  /** Centre value, already formatted - e.g. '27.4%', 'T-10', '--'. */
  value: string;
  /** Small uppercase caption under the value. */
  caption?: string;
  /** Outer diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  stroke?: number;
  /** Arc colour. Defaults by `dark`. undefined = use the default. */
  color?: string | undefined;
  /** Track colour. Defaults by `dark`. undefined = use the default. */
  track?: string | undefined;
  /** Centre value colour. Defaults by `dark`. undefined = use the default. */
  valueColor?: string | undefined;
  /** Dark context (glass card) vs light (dashboard row). */
  dark?: boolean;
}

/** Threshold colour for a rate gauge - FIT-screening benchmarks. */
export function rateColor(pct: number | null, dark = false): string {
  if (pct == null) return dark ? 'rgba(255,255,255,0.22)' : 'var(--ink-300)';
  if (pct >= 60) return dark ? '#43C28A' : 'var(--emerald)';
  if (pct >= 30) return dark ? '#3CC8C1' : 'var(--teal)';
  if (pct >= 15) return dark ? '#E3A949' : 'var(--amber)';
  return dark ? '#DC6F55' : 'var(--crimson)';
}

export function RingGauge({
  pct,
  value,
  caption,
  size = 132,
  stroke = 11,
  color,
  track,
  valueColor,
  dark = false,
}: RingGaugeProps) {
  const reduced = usePrefersReducedMotion();
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = pct == null ? 0 : Math.max(0, Math.min(100, pct));

  // Animate the sweep from 0 on mount unless reduced motion is requested.
  const [shown, setShown] = useState(reduced ? clamped : 0);
  useEffect(() => {
    if (reduced) {
      setShown(clamped);
      return;
    }
    const id = window.requestAnimationFrame(() => setShown(clamped));
    return () => window.cancelAnimationFrame(id);
  }, [clamped, reduced]);

  const dash = (shown / 100) * circ;
  const arcColor = color ?? rateColor(pct, dark);
  const trackColor = track ?? (dark ? 'rgba(255,255,255,0.10)' : 'var(--paper-3)');
  const vColor = valueColor ?? (dark ? '#F7F5EF' : 'var(--ink-950)');

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        {pct != null && clamped > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={arcColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ - dash}`}
            style={{ transition: reduced ? 'none' : 'stroke-dasharray 760ms cubic-bezier(.2,.7,.2,1)' }}
          />
        )}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        <span
          className="tab-num"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: Math.round(size * 0.25),
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: vColor,
          }}
        >
          {value}
        </span>
        {caption && (
          <span
            className="mono"
            style={{
              fontSize: 8.5,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: dark ? 'rgba(247,245,239,0.6)' : 'var(--ink-400)',
              textAlign: 'center',
              maxWidth: size * 0.82,
              lineHeight: 1.3,
            }}
          >
            {caption}
          </span>
        )}
      </div>
    </div>
  );
}
